import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SignInBottomModal from 'Components/SignInBottomModal';
import MenuShortcut from './MenuShortcut';

import useI18n from 'i18n';
import { useBridgeApi, useSelector } from 'Hooks';
import CategoryItem from './CategoryItem';
import Row from './Row';
import { checkOutOfStock } from '../../../utils/inventory';
import {
  getAllCategories,
  getAllCategoriesVariables,
  getAllCategories_allCategories_items as Item,
} from './__queries__/getAllCategories';
import { useShoppingCartApi } from 'Components/ShoppingCartUniverse';
import PlaceholderItem from './PlaceholderItem';
import useEntryFinder from 'Hooks/useEntryFinder';
import { useStorage } from 'Components/Storage';
import { useConfig } from 'Components/ConfigProvider';

const Padding = styled.div`
  height: 40px;
  background: #f8f8fb;
`;

export const CATEGORIES_QUERY = gql`
  query getAllCategories($companyId: ID!, $maxCategories: Int!, $maxItems: Int!) {
    allCategories(
      orderBy: order_DESC
      first: $maxCategories
      filter: { company: { id: $companyId }, deleted: false }
    ) {
      id
      order
      name
      nameLang {
        id
        se
        en
        no
      }
      items(orderBy: order_DESC, first: $maxItems, filter: { deleted: false }) {
        id
        order
        disableMembershipDiscount
        title
        titleLang {
          id
          se
          en
          no
        }
        descriptionLang {
          id
          se
          en
          no
        }
        defaultPrice
        image {
          id
          file {
            id
            url
          }
        }
      }
    }
  }
`;

/**
 * Home page categories component
 */
const Categories = () => {
  const config = useConfig();
  const maxCategories = config.MAX_CATEGORIES ?? 3;
  const maxItems = 5;
  const params = useParams<{ venue_id?: string }>();
  const venueId = params?.venue_id || '';
  const { i18n } = useI18n();
  const { push } = useHistory();
  const api = useBridgeApi();
  const venue = useSelector((state) => state.venue);
  const qrOrder = useSelector((state) => state.qrOrder);
  const shoppingCartApi = useShoppingCartApi();
  const findEntryFromItem = useEntryFinder();
  const inventory = useSelector((state) => state.inventory ?? []);
  const [storageData] = useStorage();
  const authToken = storageData.authToken;
  const isLoggedIn = !!authToken;
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { data } = useQuery<getAllCategories, getAllCategoriesVariables>(CATEGORIES_QUERY, {
    variables: {
      companyId: config.COMPANY_ID,
      maxCategories,
      maxItems,
    },
  });

  const firstCategories = useMemo(() => {
    return (data?.allCategories || [])
      .map((category) => {
        return {
          id: category.id,
          name: category.name,
          nameLang: category.nameLang,
          items: category.items.filter((item) => findEntryFromItem(item.id)).slice(0, maxItems),
        };
      })
      .filter(({ items }) => items.length > 0)
      .slice(0, 3);
  }, [data, venue, findEntryFromItem]);

  const handleClick = (item: Item) => {
    const entry = findEntryFromItem(item.id);

    // Joar: Don't think this would ever happen since we filter by entry above
    if (!entry) {
      return;
    }

    // If we are not adding to cart right away, redirect to item page
    if (!entry.instant) {
      push(`/order/${venueId}/item/${item.id}`);
      return;
      // Allow "Anonymous Purchase" check.
    } else if (!config.ANONYMOUS_PURCHASE && !isLoggedIn && (!qrOrder || !storageData.qrOrder)) {
      // If restaurant doesn't allow anonymous purchases & the user is not logged in, show login modal (Unless it's table ordering).
      setShowLoginModal(true);
      return;
    }

    toast(i18n('Toast.Add', i18n(item.titleLang)), {
      position: 'bottom-center',
      autoClose: 1000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
    });
    api.vibrate('impactLight');
    if (entry && item) {
      shoppingCartApi.addItem(
        item.titleLang.se,
        entry.item.id,
        entry.id,
        [],
        [],
        entry.price,
        item.disableMembershipDiscount,
      );
    }
  };

  if (!firstCategories.length) {
    return (
      <>
        <Row title="Laddar...">
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
        </Row>
        <Row title="Laddar...">
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
        </Row>
        <Row title="Laddar...">
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
        </Row>
      </>
    );
  }

  const toMenu = (categoryId?: string) => {
    return categoryId
      ? push(`/order/${venueId}/menu/${categoryId}`)
      : push(`/order/${venueId}/menu`);
  };

  return (
    <>
      {firstCategories.map(({ id, nameLang, items }) => {
        return (
          <Row key={id} title={i18n(nameLang)} categoryId={id} toMenu={toMenu}>
            {items.map((item) => {
              // This will never return null
              const entry = findEntryFromItem(item.id)!;

              return (
                <CategoryItem
                  key={item.id}
                  title={item.titleLang}
                  price={entry.price}
                  image={item?.image?.file?.url}
                  onClick={() => handleClick(item)}
                  outOfStock={checkOutOfStock(item.id, inventory)}
                />
              );
            })}
          </Row>
        );
      })}
      <MenuShortcut toMenu={toMenu} />
      <Padding />
      <SignInBottomModal show={showLoginModal} setShow={setShowLoginModal} />
    </>
  );
};

export default Categories;
